/**
 * The player's board: what they have shaded, and what is wrong with it.
 *
 * Checking is done against the *rules*, never against the stored solution. The
 * puzzle has exactly one solution, so the two agree on a finished board — but
 * only the rules say anything useful about an unfinished one, which is the whole
 * point of checking as you go. It also means a square shaded the wrong way is
 * not flagged until it actually breaks something, so the board never quietly
 * tells the player the answer.
 *
 * Every fault here is a rule that is already broken and cannot be talked out of:
 * a region past the size its number claims, two numbers inside one region, a
 * region closed off with no number in it, or one closed off too small. A region
 * that is merely unfinished is not a fault, and is left alone.
 */

import { geometry, regionsOf } from './board';
import { Deducer, type Deduction } from './solver';
import {
  LIGHT,
  MAX_REGION,
  SHADOW,
  UNKNOWN,
  type Clue,
  type Mark,
  type Puzzle,
  type Shade,
  other,
} from './types';

export interface Change {
  cell: number;
  mark: Mark;
}

export interface BoardStatus {
  /** Cells drawn as broken: they sit in a region that breaks a rule. */
  errors: Set<number>;
  /** Numbers whose region is currently exactly the size they claim. */
  satisfied: Set<number>;
  solved: boolean;
  /** Cells the player has decided, numbers included. */
  filled: number;
}

/** The numbers are on the board from the start; the rest is the player's. */
export function startingMarks(puzzle: Puzzle): Mark[] {
  const marks = new Array<Mark>(puzzle.width * puzzle.height).fill(UNKNOWN);
  for (const clue of puzzle.clues) marks[clue.cell] = clue.shade;
  return marks;
}

/**
 * Advances one cell through undecided → light → shadow → undecided.
 *
 * Light first because it is the smaller change: an undecided square is already
 * pale, so the first tap tints it rather than filling it in, and a player
 * sweeping out the shape of a region sees it appear rather than a wall of dark.
 */
export function cycleMark(mark: Mark): Mark {
  if (mark === UNKNOWN) return LIGHT;
  if (mark === LIGHT) return SHADOW;
  return UNKNOWN;
}

/**
 * Writes changes into the marks, leaving the numbers where they are.
 *
 * Returns the same array when nothing moves, so the caller can use identity to
 * decide whether this is worth an undo entry.
 */
export function applyMarks(
  puzzle: Puzzle,
  marks: readonly Mark[],
  changes: readonly Change[],
): Mark[] {
  const given = new Set(puzzle.clues.map((clue) => clue.cell));
  const next = marks.slice();
  let touched = false;
  for (const change of changes) {
    // A number's own square states which shade its region is, so it is not the
    // player's to change — and a tap that landed on one would otherwise erase
    // the clue's shading and take its region with it.
    if (given.has(change.cell)) continue;
    if (next[change.cell] === change.mark) continue;
    next[change.cell] = change.mark;
    touched = true;
  }
  return touched ? next : (marks as Mark[]);
}

export function analyseBoard(puzzle: Puzzle, marks: readonly Mark[]): BoardStatus {
  const geo = geometry(puzzle.width, puzzle.height);
  const clueAt = new Map<number, number>();
  puzzle.clues.forEach((clue, index) => clueAt.set(clue.cell, index));

  const errors = new Set<number>();
  const satisfied = new Set<number>();
  let filled = 0;
  for (const mark of marks) if (mark !== UNKNOWN) filled += 1;

  for (const region of regionsOf(geo, marks)) {
    const inside = region.cells
      .map((cell) => clueAt.get(cell))
      .filter((index): index is number => index !== undefined);
    // A region still touching an undecided square can grow, so its size is not
    // yet a claim about anything.
    const open = region.cells.some((cell) =>
      (geo.neighbors[cell] as number[]).some((neighbor) => marks[neighbor] === UNKNOWN),
    );

    if (inside.length > 1) {
      for (const cell of region.cells) errors.add(cell);
      continue;
    }
    if (inside.length === 0) {
      if (!open) for (const cell of region.cells) errors.add(cell);
      continue;
    }
    const index = inside[0] as number;
    const value = (puzzle.clues[index] as Clue).value;
    // A `?` claims only that its block is some legal size, so the only way to
    // break one is to grow it past the largest number there is — and the only
    // way to finish one is to close it, since while it can still grow there is
    // no size it is committed to. That is the difference a `?` makes to the
    // player: a numbered block is answerable on its own, and this one is not.
    const ceiling = value ?? MAX_REGION;
    const floor = value ?? 1;
    if (region.cells.length > ceiling || (!open && region.cells.length < floor)) {
      for (const cell of region.cells) errors.add(cell);
      continue;
    }
    if (value === null ? !open : region.cells.length === value) satisfied.add(index);
  }

  const solved =
    filled === geo.cells && errors.size === 0 && satisfied.size === puzzle.clues.length;
  return { errors, satisfied, solved, filled };
}

/**
 * The shading that follows from a region that is already the right size.
 *
 * Used by "fill", which puts in the marks the player would otherwise place by
 * hand: a region that has reached its number cannot grow, so everything around
 * it is the other shade. Nothing here is a deduction the player has not already
 * made — the region they finished *is* the deduction.
 */
export function impliedEdges(puzzle: Puzzle, marks: readonly Mark[]): Change[] {
  const geo = geometry(puzzle.width, puzzle.height);
  const clueAt = new Map<number, number>();
  puzzle.clues.forEach((clue, index) => clueAt.set(clue.cell, index));

  const changes = new Map<number, Change>();
  for (const region of regionsOf(geo, marks)) {
    const inside = region.cells
      .map((cell) => clueAt.get(cell))
      .filter((index): index is number => index !== undefined);
    if (inside.length !== 1) continue;
    const clue = puzzle.clues[inside[0] as number] as Clue;
    // A `?` never reaches its number, because it has not been told one. There is
    // nothing to fill in around it either: the only way to know one is finished
    // is to have already closed it in by hand.
    if (clue.value === null || region.cells.length !== clue.value) continue;
    for (const cell of region.cells) {
      for (const neighbor of geo.neighbors[cell] as number[]) {
        if (marks[neighbor] !== UNKNOWN) continue;
        changes.set(neighbor, { cell: neighbor, mark: other(clue.shade) });
      }
    }
  }
  return [...changes.values()];
}

// ── Hints ───────────────────────────────────────────────────────────────────

export interface Hint {
  message: string;
  /** Cells to highlight. Where to look, rather than what to put there. */
  cells: number[];
}

function where(puzzle: Puzzle, cell: number): string {
  return `row ${Math.floor(cell / puzzle.width) + 1}, column ${(cell % puzzle.width) + 1}`;
}

/** A shade as the screen names it: "light", and "dark" rather than "shadow". */
function shadeWord(shade: Shade): string {
  return shade === LIGHT ? 'light' : 'dark';
}

/** "The 4 at row 2, column 3", or "The ? at …" for a hidden number. */
function nameClue(puzzle: Puzzle, clue: Clue): string {
  return `The ${clue.value ?? '?'} at ${where(puzzle, clue.cell)}`;
}

/**
 * What a number with one shape left is telling the player, taught.
 *
 * Three sentences, in the order a teacher would put them: what is true, why,
 * and what to do about it. The last one is the half players forget — a
 * finished block is fenced in by the other shade — so it is always said, and
 * said with its reason, since the reason is the rule the whole game runs on.
 */
function onlyShape(puzzle: Puzzle, clue: Clue): string {
  const named = nameClue(puzzle, clue);
  if (clue.value === 1) {
    return (
      `${named} is a block all on its own: one square, already there. ` +
      'So every square touching it has to be the other shade — if one matched, ' +
      'it would join the block and make it bigger than 1.'
    );
  }
  if (clue.value === null) {
    return (
      `${named} has only one shape left that fits. You are not told its size, but ` +
      'the squares around it leave room for just one block, so that block is its ' +
      'region, whatever size it comes to. Shade it in, then fence it: every square ' +
      'touching it from outside takes the other shade.'
    );
  }
  return (
    `${named} has only one shape left that fits. Its block needs exactly ${clue.value} ` +
    `squares, and between the edges, the shading so far and the numbers nearby there is ` +
    `only one way to draw it. Shade that block ${shadeWord(clue.shade)}, then fence it in: ` +
    `every square touching it from outside takes the other shade, or the block would grow ` +
    `past ${clue.value}.`
  );
}

/**
 * What several shapes agreeing is telling the player, taught.
 *
 * Which of the two agreements it is decides the explanation, and the
 * deduction says which: a square taking the number's own shade is one every
 * shape covers, and a square taking the other is one every shape touches from
 * outside. The suggestion to sketch two or three of the shapes is the actual
 * technique — nobody holds every shape of a 5 in their head, and nobody has to.
 */
function sharedShapes(puzzle: Puzzle, clue: Clue, step: Deduction): string {
  const named = nameClue(puzzle, clue);
  const inside = step.assignments.some((assignment) => assignment.shade === clue.shade);
  const outside = step.assignments.some((assignment) => assignment.shade !== clue.shade);
  const size = clue.value === null ? 'its block' : `a block of ${clue.value}`;
  const opening =
    `${named} could still take several shapes, but they agree about something. ` +
    'Sketch two or three of the ways it could go and compare them.';
  if (inside && outside) {
    return (
      `${opening} There is a square every one of them uses, which has to be ` +
      `${shadeWord(clue.shade)} like the number; and a square every one of them touches from ` +
      `outside, which has to be the other shade, since it would otherwise join ${size} ` +
      'and make it too big.'
    );
  }
  if (inside) {
    return (
      `${opening} There is a square every one of them uses — so whichever shape it turns ` +
      `out to be, that square is part of it, and it is ${shadeWord(clue.shade)} like the number.`
    );
  }
  return (
    `${opening} There is a square every one of them touches from outside without including. ` +
    `It cannot be ${shadeWord(clue.shade)}: it would join ${size} and make it too big. ` +
    'So it takes the other shade.'
  );
}

/**
 * Where to look next, and why, in the words of someone teaching the game.
 *
 * A mark in the wrong place makes every deduction after it meaningless, so those
 * are found first, and they are the one place a hint consults the solution. The
 * fault checker cannot flag them: a square shaded the wrong way but not yet
 * touching anything breaks no rule, and saying so unprompted would give the game
 * away.
 *
 * After that the ladder in `solver.ts` runs from what the player has shaded, and
 * the hint names what to look at, the rule that applies, and why it forces
 * something — the reasoning, which is what the player is short of, and not the
 * answer, which they can then find. It never names the shade of the square the
 * reasoning settles unless the reasoning itself does ("every shape uses this
 * square, so it is the number's shade"), and it highlights the number or the
 * square to start from rather than the squares to fill.
 *
 * A trial names the number it will run into, when it runs into one. "Try a
 * shade and see what breaks" is honest but it is not teaching: the useful part
 * is knowing what to watch, and the player still has to work out which of the
 * two shades is the one that breaks it.
 */
export function nextHint(puzzle: Puzzle, marks: readonly Mark[]): Hint {
  const status = analyseBoard(puzzle, marks);
  if (status.solved) return { message: 'This one is already finished.', cells: [] };

  const wrong: number[] = [];
  for (let cell = 0; cell < marks.length; cell += 1) {
    const mark = marks[cell] as Mark;
    if (mark !== UNKNOWN && mark !== puzzle.solution[cell]) wrong.push(cell);
  }
  if (wrong.length > 0) {
    return {
      message:
        wrong.length === 1
          ? 'Careful — the highlighted square is the wrong shade. Anything worked out from it ' +
            'will be off too, so clear it and look again from there.'
          : `Careful — ${wrong.length} squares are the wrong shade. They are highlighted; ` +
            'clear them before going on, since anything worked out from them will be off too.',
      cells: wrong,
    };
  }

  const deducer = new Deducer(puzzle, marks);
  const step = deducer.next() ?? deducer.trial();
  if (!step) {
    return {
      message:
        'Nothing more follows from the shading so far. Try starting again from a different ' +
        'number, and look for one whose shapes are running out of room.',
      cells: [],
    };
  }

  if (step.clue !== null) {
    const clue = puzzle.clues[step.clue] as Clue;
    return {
      message: step.rung === 1 ? onlyShape(puzzle, clue) : sharedShapes(puzzle, clue, step),
      cells: [clue.cell],
    };
  }

  const cell = step.cell as number;
  if (step.rung === 3) {
    return {
      message:
        'Every square has to belong to some number’s block. Look at the highlighted square ' +
        'and ask which numbers could still stretch far enough to include it. Only numbers of ' +
        'one shade can — the others are too small, too far away, or walled off — so the ' +
        'square takes their shade.',
      cells: [cell],
    };
  }

  const opening =
    'There is nothing left to read straight off the board, so it is time to test an idea. ' +
    'Give the highlighted square one shade and follow what that forces, one step at a time.';
  const closing = 'When that happens you have learned something: the square is the other shade.';
  if (step.breaks !== undefined && step.breaks !== null) {
    const clue = puzzle.clues[step.breaks] as Clue;
    return {
      message:
        `${opening} One of the two shades leaves ${nameClue(puzzle, clue).replace(/^The/, 'the')} ` +
        `with no shape that fits. ${closing}`,
      cells: [cell, clue.cell],
    };
  }
  if (step.breaksAt === cell) {
    return {
      message:
        `${opening} One of the two shades cuts the square off: follow it through and no ` +
        `number of that shade can reach it any more. ${closing}`,
      cells: [cell],
    };
  }
  if (step.breaksAt !== undefined && step.breaksAt !== null) {
    return {
      message:
        `${opening} One of the two shades leaves the square at ${where(puzzle, step.breaksAt)} ` +
        `with no number able to reach it. ${closing}`,
      cells: [cell, step.breaksAt],
    };
  }
  return {
    message: `${opening} One of the two shades runs into a rule it breaks. ${closing}`,
    cells: [cell],
  };
}
